import { Injectable } from '@angular/core';
import { authGuard } from '@app/guards/auth.guard';
import { loginGuard } from '@app/guards/login.guard';
import { userGuard } from '@app/guards/user.guard';
import {
    TRouteAccessConfig,
    TChildrenPageType,
    TPageType,
    TRouteAccess,
    TAllPageTypes,
} from '@app/interfaces/route.interface';
import { UserModel } from '@app/models/user.model';
import { TUser } from '@app/types/user.type';
import { translations } from '@app/utils/translations';

@Injectable({
    providedIn: 'root',
})
export class AppRouteService {
    get routeAccess(): TRouteAccess {
        return {
            auth: {
                canActivate: [loginGuard],
                userTypes: [],
                routeConfig: {
                    path: 'auth',
                    children: {
                        'sign-in': {
                            canActivate: [],
                            userTypes: [],
                            routeConfig: {
                                path: 'sign-in',
                            },
                        },
                        'forgot-password': {
                            canActivate: [],
                            userTypes: [],
                            routeConfig: {
                                path: 'forgot-password',
                            },
                        },
                        'landing': {
                            canActivate: [],
                            userTypes: [],
                            routeConfig: {
                                path: 'landing',
                            },
                        },
                    },
                },
            },
            users: {
                canActivate: [authGuard, userGuard],
                userTypes: ['Admin', 'Technical', 'FacilityManager'],
                routeConfig: {
                    path: 'users',
                },
                title: translations.users.title,
                icon: 'user',
                visibleOnMenu: true,
            },
            elev8: {
                canActivate: [authGuard, userGuard],
                userTypes: ['Admin', 'Technical', 'Support', 'Viewer'],
                routeConfig: {
                    path: 'elev8',
                    children: {
                        ':id': {
                            canActivate: [authGuard, userGuard],
                            userTypes: ['Admin', 'Technical', 'Support', 'Viewer'],
                            routeConfig: {
                                path: ':id',
                            },
                            title: translations.elev8.menu,
                        },
                    },
                },
                title: translations.elev8.menu,
                icon: 'large_screen',
                visibleOnMenu: true,
            },
            management: {
                canActivate: [authGuard, userGuard],
                userTypes: ['Admin', 'Technical', 'Support', 'Viewer'],
                routeConfig: {
                    path: 'management',
                    children: {
                        'screens': {
                            canActivate: [authGuard, userGuard],
                            userTypes: ['Admin', 'Technical', 'Support', 'Viewer'],
                            routeConfig: {
                                path: 'screens',
                            },
                            title: translations.management.screens.title,
                            visibleOnMenu: true,
                        },
                        'screens/:screenId': {
                            canActivate: [authGuard, userGuard],
                            userTypes: ['Admin', 'Technical', 'Support', 'Viewer'],
                            routeConfig: {
                                path: 'screens/:screenId',
                            },
                            title: translations.management.screens.title,
                        },
                        'frontlines': {
                            canActivate: [authGuard, userGuard],
                            userTypes: ['Admin'],
                            routeConfig: {
                                path: 'frontlines',
                            },
                            title: translations.management.countries.title,
                            visibleOnMenu: true,
                        },
                        'policies': {
                            canActivate: [authGuard, userGuard],
                            userTypes: ['Admin'],
                            routeConfig: {
                                path: 'policies',
                            },
                            title: translations.management.policies.title,
                            visibleOnMenu: true,
                        },
                        'policies/:policyId': {
                            canActivate: [authGuard, userGuard],
                            userTypes: ['Admin'],
                            routeConfig: {
                                path: 'policies/:policyId',
                            },
                            title: translations.management.policies.title,
                        },
                    },
                },
                title: translations.management.title,
                icon: 'maintanence-2',
                visibleOnMenu: true,
            },
        };
    }

    get defaultRoute(): TPageType {
        return 'users';
    }

    getMenuRoutes(): string[] {
        const getChildrenPaths = (page: TPageType): string[] => {
            const children: Partial<
                Record<TChildrenPageType[TPageType], TRouteAccessConfig<TAllPageTypes>>
            > = this.routeAccess[page]?.routeConfig.children ?? {};
            return Object.values(children)
                .filter(child => child.visibleOnMenu)
                .map(child => this.getPath(page) + '/' + child.routeConfig.path);
        };

        const parentRoutes = Object.keys(this.routeAccess)
            .filter((page: string) => this.routeAccess[page as TPageType].visibleOnMenu)
            .map((page: string) => this.getPath(page as TPageType));

        return parentRoutes.concat(
            ...Object.keys(this.routeAccess).map(page => getChildrenPaths(page as TPageType)),
        );
    }

    getRouteAvailability<T extends TAllPageTypes>(
        config: TRouteAccessConfig<T>,
        user: UserModel,
    ): boolean {
        const userType: TUser | undefined = user.role;

        // skip route not visible
        if (!config.visibleOnMenu) return false;

        // default/general routes always should be shown if route config has as router type
        if (config.userTypes.some((type: string) => type == userType)) return true;

        return false;
    }

    getPath<K extends TPageType>(mainPath: K, children?: TChildrenPageType[K]): string {
        return '/' + mainPath + (children ? '/' + children : '');
    }

    getTitle<T extends TPageType>(
        page: T,
        child?: TChildrenPageType[T] extends string ? TChildrenPageType[T] : never,
    ): string {
        const children = this.routeAccess[page].routeConfig.children as Record<
            string,
            { title?: string }
        >;
        if (child && children && typeof children[child] === 'object') {
            return children[child].title ?? '';
        }
        return this.routeAccess[page].title ?? '';
    }

    canActivate(mainPath: TPageType, url: string, user: UserModel): boolean {
        const userType: TUser = user.role;
        const mainRoute = this.routeAccess[mainPath];

        // Check if the user type is allowed on the main route
        const canAccessMainRoute: boolean =
            mainRoute.userTypes.some(type => type === userType) && user.tool;

        if (!canAccessMainRoute) return false;

        // Directly return true if it's an exact match
        if (url === this.getPath(mainPath)) return true;

        // If the main route does not have any children, the user can access it
        if (!mainRoute.routeConfig.children) return true;

        // Check each child route to see if it matches the given URL
        const children: Partial<
            Record<TChildrenPageType[TPageType], TRouteAccessConfig<TAllPageTypes>>
        > = mainRoute.routeConfig.children;
        for (const childRoute of Object.values(children)) {
            if (this.matchUrl(mainPath, childRoute.routeConfig.path, url)) {
                // If the child route does not specify user types, inherit access from the main route
                if (!childRoute.userTypes) {
                    return true;
                }
                // Check if the user type is allowed on the child route
                return childRoute.userTypes.some(type => type === userType);
            }
        }

        // If no child routes match the URL, the user cannot access it
        return false;
    }

    private matchUrl(mainPath: TPageType, path: string, url: string) {
        const patternedPath: string = this.getPath(mainPath) + '/' + path;
        // Check if the path uses a generic parameter pattern (e.g., /:anything)
        if (path.includes(':')) {
            // Create a regex pattern to match parameterized paths, capturing segments after '/:'
            const regexPattern = patternedPath.replace(/\/:([^/]+)/g, '/([^/]+)') + '.*';

            const regex = new RegExp('^' + regexPattern + '$');

            return regex.test(url);
        } else {
            const baseUrl = url.split('?')[0];
            return patternedPath === baseUrl;
        }
    }
}
